import { useState, useEffect, useRef } from "react";
import { LANGUAGES } from "../data/constants";
import { getLangFont } from "../utils/helpers";

/**
 * LangDropdown
 * A sticky language-picker button that opens a scrollable list of
 * all 12 supported languages with flag, native name, and English label.
 *
 * Props:
 *   lang    {string}   – currently active language code
 *   setLang {function} – callback to update the language
 */
export default function LangDropdown({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cur = LANGUAGES.find((l) => l.code === lang);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", zIndex: 300 }}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display:     "flex",
          alignItems:  "center",
          gap:         6,
          padding:     "6px 12px",
          borderRadius: 20,
          border:      "1.5px solid #d1c7b0",
          background:  "#fff",
          fontFamily:  "'DM Sans', sans-serif",
          fontSize:    13,
          fontWeight:  600,
          cursor:      "pointer",
          color:       "#444",
          boxShadow:   "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        <span style={{ fontSize: 16 }}>{cur?.flag}</span>
        <span
          style={{
            fontFamily:    getLangFont(lang),
            maxWidth:      65,
            overflow:      "hidden",
            textOverflow:  "ellipsis",
            whiteSpace:    "nowrap",
          }}
        >
          {cur?.nativeLabel}
        </span>
        <span style={{ fontSize: 8, color: "#aaa" }}>{open ? "▲" : "▼"}</span>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position:    "absolute",
            right:       0,
            top:         "calc(100% + 8px)",
            background:  "#fff",
            borderRadius: 14,
            border:      "1px solid #e8e0d0",
            boxShadow:   "0 10px 40px rgba(0,0,0,0.18)",
            width:       220,
            overflow:    "hidden",
            animation:   "fadeIn 0.15s ease",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding:       "8px 14px",
              fontSize:      10,
              fontWeight:    700,
              color:         "#9ca3af",
              letterSpacing: 1,
              textTransform: "uppercase",
              borderBottom:  "1px solid #f0ede6",
              fontFamily:    "'DM Sans', sans-serif",
            }}
          >
            🌐 Choose Language
          </div>

          {/* Language list */}
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {LANGUAGES.map((l) => (
              <div
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                style={{
                  display:    "flex",
                  alignItems: "center",
                  gap:        10,
                  padding:    "10px 14px",
                  cursor:     "pointer",
                  background: lang === l.code ? "#f0f7f4" : "transparent",
                  borderLeft: `3px solid ${lang === l.code ? "#1a4d2e" : "transparent"}`,
                }}
                onMouseEnter={(e) => { if (lang !== l.code) e.currentTarget.style.background = "#faf7f0"; }}
                onMouseLeave={(e) => { if (lang !== l.code) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontSize: 20 }}>{l.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: l.font, fontSize: 14, color: "#1a1a1a", fontWeight: 500 }}>
                    {l.nativeLabel}
                  </div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9ca3af" }}>
                    {l.label}
                  </div>
                </div>
                {lang === l.code && (
                  <span style={{ color: "#1a4d2e", fontSize: 13, fontWeight: 700 }}>✓</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
